import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import axios from 'axios';
import {
  SparklesIcon,
  StarIcon,
  ArrowRightIcon,
  MapPinIcon,
  GiftIcon,
  CheckCircleIcon,
  CheckIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { useCurrency } from '../contexts/CurrencyContext';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// Custom Swiper styles for testimonials
const testimonialsSwiperStyles = `
  .testimonials-swiper .swiper-pagination {
    bottom: 0 !important;
  }
  .testimonials-swiper .swiper-pagination-bullet {
    width: 12px !important;
    height: 12px !important;
    margin: 0 6px !important;
  }
  .testimonials-swiper .swiper-slide {
    height: auto !important;
  }
  .testimonials-swiper .swiper-wrapper {
    align-items: stretch !important;
  }
`;

// Custom Swiper styles for packages carousel
const packagesSwiperStyles = `
  .packages-swiper .swiper-pagination {
    bottom: -10px !important;
  }
  .packages-swiper .swiper-pagination-bullet {
    width: 10px !important;
    height: 10px !important;
    margin: 0 5px !important;
    background: #f97316 !important;
    opacity: 0.4 !important;
  }
  .packages-swiper .swiper-pagination-bullet-active {
    opacity: 1 !important;
    transform: scale(1.2);
  }
  .packages-swiper .swiper-slide {
    height: auto !important;
  }
  .packages-swiper .swiper-wrapper {
    align-items: stretch !important;
  }
  .packages-swiper .swiper-button-prev,
  .packages-swiper .swiper-button-next {
    color: #f97316 !important;
    background: white !important;
    width: 44px !important;
    height: 44px !important;
    border-radius: 50% !important;
    box-shadow: 0 4px 15px rgba(0,0,0,0.1) !important;
  }
  .packages-swiper .swiper-button-prev:after,
  .packages-swiper .swiper-button-next:after {
    font-size: 18px !important;
    font-weight: bold !important;
  }
  .packages-swiper .swiper-button-disabled {
    opacity: 0.3 !important;
  }
`;

interface Package {
  _id: string;
  name: string;
  description: string;
  bannerImage: string;
  priceUSD: number;
  priceAUD: number;
  lastMinuteDealPriceUSD?: number;
  lastMinuteDealPriceAUD?: number;
  lastMinuteDealExpiryDate?: string;
  hasActiveLastMinuteDeal?: boolean;
  validityPeriod: number;
  popular: boolean;
  isActive: boolean;
  isLive: boolean;
  destination: string | { _id: string; name: string };
}

interface Destination {
  _id: string;
  name: string;
  image: string;
  description?: string;
  packageCount?: number;
  showInMainNav?: boolean;
}

interface Testimonial {
  _id: string;
  customerName: string;
  review: string;
  rating: number;
  date: string;
  location: string;
  resortName: string;
  packageName?: string;
  packageCode?: string;
}

interface LastMinuteDeal {
  _id: string;
  title: string;
  description?: string;
  image: string;
  link: string;
  linkType: 'internal' | 'external';
  discountBadge?: string;
}

interface HomePageData {
  featuredDestinations: Destination[];
  popularPackages: Package[];
  stats: {
    totalDestinations: number;
    totalPackages: number;
    totalResorts: number;
    totalBookings: number;
  };
  testimonials: Testimonial[];
}

// Normalize image URLs to work in dev and prod, and to support server-served uploads
const resolveImageUrl = (img?: string) => {
  if (!img) return '';
  const normalized = String(img).replace(/\\/g, '/');

  // If Cloudinary URL, return as-is
  if (normalized.includes('cloudinary.com')) {
    return normalized;
  }

  // If absolute URL or data URL, return as-is
  if (/^(https?:)?\/\//i.test(normalized) || normalized.startsWith('data:')) {
    return normalized;
  }

  // Handle relative paths
  const idx = normalized.indexOf('/uploads/');
  const relPath = idx !== -1 ? normalized.slice(idx) : (normalized.startsWith('/') ? normalized : `/${normalized}`);
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const base = isDev ? 'http://localhost:5000' : '';
  return `${base}${relPath}`;
};

const HomePage: React.FC = () => {
  const { formatPrice, currency } = useCurrency();

  // Helper function to get the display price in AUD only (last minute deal if available, otherwise regular price)
  const getDisplayPrice = (pkg: Package) => {
    // Check if deal exists and is valid (check price exists and expiry date is in future)
    const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
    const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
    const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
    
    if (hasDealPrice && isDealActive && dealPriceAUD) {
      return {
        price: `A$${dealPriceAUD.toLocaleString()}`,
        originalPrice: `A$${pkg.priceAUD.toLocaleString()}`,
        isDeal: true,
        savings: `A$${(pkg.priceAUD - dealPriceAUD).toFixed(0)}`
      };
    }
    return {
      price: `A$${pkg.priceAUD.toLocaleString()}`,
      originalPrice: null,
      isDeal: false,
      savings: null
    };
  };
  const [homeData, setHomeData] = useState<HomePageData | null>(null);
  const [lastMinuteDeals, setLastMinuteDeals] = useState<LastMinuteDeal[]>([]);
  const [loading, setLoading] = useState(true);
  // Motion preference and user toggle
  const prefersReduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [animationsEnabled, setAnimationsEnabled] = useState<boolean>(!prefersReduced);
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  // Two-layer crossfade background videos for smooth transitions
  const bgARef = useRef<HTMLVideoElement | null>(null);
  const bgBRef = useRef<HTMLVideoElement | null>(null);
  const bgPlaylist = ['/videos/bali-bg1.mp4', '/videos/bali-bg2.mp4'];
  const [srcA, setSrcA] = useState<string>(bgPlaylist[0]);
  const [srcB, setSrcB] = useState<string>(bgPlaylist[1]);
  const [activeLayer, setActiveLayer] = useState<0 | 1>(0);
  // Remove unused activeIndex to fix eslint warning
  // const [activeIndex, setActiveIndex] = useState<number>(0);
  const [nextIndex, setNextIndex] = useState<number>(1);
  const prefetchRef = useRef<boolean>(false);
  const FADE_MS = 2000; // faster crossfade for better performance
  const SLOW_RATE = 0.3; // slower, romantic playback
  // Reveal hero content immediately for better performance
  const [showHeroContent, setShowHeroContent] = useState<boolean>(true);
  // Cinematic staged sequence control: 0=logo, 1=line1, 2=line2, 3=subtitle, 4=buttons
  const [titleStage, setTitleStage] = useState<number>(0);
  const shouldAnimateText = animationsEnabled && !prefersReduced;
  const stageTimeoutRef = useRef<number | null>(null);
  const scheduleStage = (nextStage: number, delay = 0) => {
    if (stageTimeoutRef.current) window.clearTimeout(stageTimeoutRef.current);
    stageTimeoutRef.current = window.setTimeout(() => setTitleStage(nextStage), delay);
  };

  // Fast parallel API loading with timeout and fallback
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        // Set timeout to 60 seconds (Render free tier cold start can take 30+ seconds)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('API timeout')), 60000)
        );

        // Parallel API calls with timeout
        const [homeResponse, dealsResponse] = await Promise.allSettled([
          Promise.race([axios.get('/api/home/data'), timeoutPromise]),
          Promise.race([axios.get('/api/last-minute-deals'), timeoutPromise])
        ]);

        // Process home data
        if (homeResponse.status === 'fulfilled') {
          const payload = (homeResponse.value as any).data?.data || {};
          if (payload.popularPackages && payload.popularPackages.length > 0) {
            payload.popularPackages = payload.popularPackages.map((pkg: any) => ({
              ...pkg,
              priceUSD: pkg.priceUSD || pkg.price?.usd || 0,
              priceAUD: pkg.priceAUD || pkg.price?.aud || 0,
              isActive: pkg.isActive ?? pkg.active ?? true,
              isLive: pkg.isLive ?? false,
              popular: pkg.popular ?? false
            }));
          } else {
            payload.popularPackages = [];
          }
          setHomeData({
            featuredDestinations: payload.featuredDestinations || [],
            popularPackages: payload.popularPackages || [],
            stats: payload.stats || { totalDestinations: 0, totalPackages: 0, totalResorts: 0, totalBookings: 0 },
            testimonials: payload.testimonials || []
          });
        } else {
          // FALLBACK: If /api/home/data fails, fetch from individual endpoints
          console.warn('Home data endpoint failed, fetching from individual endpoints...');
          try {
            const [destinationsRes, packagesRes] = await Promise.allSettled([
              axios.get('/api/destinations'),
              axios.get('/api/packages')
            ]);

            const destinations = destinationsRes.status === 'fulfilled'
              ? (destinationsRes.value as any).data?.destinations || []
              : [];

            const packages = packagesRes.status === 'fulfilled'
              ? (packagesRes.value as any).data?.packages || []
              : [];

            // Process packages
            const processedPackages = packages.slice(0, 6).map((pkg: any) => ({
              ...pkg,
              priceUSD: pkg.priceUSD || pkg.price?.usd || 0,
              priceAUD: pkg.priceAUD || pkg.price?.aud || 0,
              isActive: pkg.isActive ?? pkg.active ?? true,
              isLive: pkg.isLive ?? false,
              popular: pkg.popular ?? false
            }));

            setHomeData({
              featuredDestinations: destinations.slice(0, 6),
              popularPackages: processedPackages,
              stats: {
                totalDestinations: destinations.length,
                totalPackages: packages.length,
                totalResorts: 0,
                totalBookings: 0
              },
              testimonials: []
            });

            console.log('✅ Loaded data from fallback endpoints');
          } catch (fallbackError) {
            console.error('Fallback endpoints also failed:', fallbackError);
            // Still set empty arrays to prevent undefined errors
            setHomeData({
              featuredDestinations: [],
              popularPackages: [],
              stats: { totalDestinations: 0, totalPackages: 0, totalResorts: 0, totalBookings: 0 },
              testimonials: []
            });
          }
        }

        // Process deals data
        if (dealsResponse.status === 'fulfilled') {
          setLastMinuteDeals((dealsResponse.value as any).data?.deals || []);
        } else {
          console.error('Failed to fetch deals:', dealsResponse.reason);
          setLastMinuteDeals([]);
        }

      } catch (error) {
        console.error('Failed to fetch data:', error);
        // Last resort: try individual endpoints
        try {
          const [destinationsRes, packagesRes] = await Promise.all([
            axios.get('/api/destinations').catch(() => ({ data: { destinations: [] } })),
            axios.get('/api/packages').catch(() => ({ data: { packages: [] } }))
          ]);

          const destinations = destinationsRes.data?.destinations || [];
          const packages = packagesRes.data?.packages || [];

          setHomeData({
            featuredDestinations: destinations.slice(0, 6),
            popularPackages: packages.slice(0, 6),
            stats: {
              totalDestinations: destinations.length,
              totalPackages: packages.length,
              totalResorts: 0,
              totalBookings: 0
            },
            testimonials: []
          });
        } catch {
          // Absolute last resort: empty data
          setHomeData({
            featuredDestinations: [],
            popularPackages: [],
            stats: { totalDestinations: 0, totalPackages: 0, totalResorts: 0, totalBookings: 0 },
            testimonials: []
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  // Cinematic video drives visuals; no RAF loop needed now

  // Close video on ESC and lock body scroll while open
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVideoOpen(false);
    };
    if (isVideoOpen) {
      document.addEventListener('keydown', onKeyDown);
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', onKeyDown);
        document.body.style.overflow = original;
      };
    }
  }, [isVideoOpen]);

  const handleCloseVideo = () => {
    setIsVideoOpen(false);
  };

  // Crossfade to the next clip smoothly
  const startCrossfade = (fromLayer: 0 | 1) => {
    if (prefetchRef.current) return;
    prefetchRef.current = true;
    const toLayer: 0 | 1 = fromLayer === 0 ? 1 : 0;
    const toRef = toLayer === 0 ? bgARef.current : bgBRef.current;
    if (toRef) {
      try {
        toRef.currentTime = 0;
        toRef.playbackRate = animationsEnabled && !prefersReduced ? SLOW_RATE : 1;
        if (animationsEnabled && !prefersReduced) {
          const p = toRef.play();
          if (p && typeof (p as any).catch === 'function') (p as Promise<void>).catch(() => { });
        }
      } catch { }
    }
    setActiveLayer(toLayer);
    window.setTimeout(() => {
      const fromRef = fromLayer === 0 ? bgARef.current : bgBRef.current;
      try { fromRef?.pause(); } catch { }
      const newNextIndex = (nextIndex + 1) % bgPlaylist.length;
      if (fromLayer === 0) setSrcA(bgPlaylist[newNextIndex]); else setSrcB(bgPlaylist[newNextIndex]);
      // setActiveIndex(nextIndex); // Removed to fix unused variable warning
      setNextIndex(newNextIndex);
      prefetchRef.current = false;
    }, FADE_MS + 50);
  };

  // No pre-crossfade; let each clip play in full and then fade to the next

  // Keep background videos in sync with motion settings; only active layer plays
  useEffect(() => {
    const refs = [bgARef.current, bgBRef.current];
    refs.forEach((vid, idx) => {
      if (!vid) return;
      vid.playbackRate = animationsEnabled && !prefersReduced ? SLOW_RATE : 1;
      if (animationsEnabled && !prefersReduced) {
        if (idx === activeLayer) {
          const p = vid.play();
          if (p && typeof (p as any).catch === 'function') (p as Promise<void>).catch(() => { });
        } else {
          // Keep the standby layer paused until we prefetch near the end
          try { vid.pause(); } catch { }
        }
      } else {
        try { vid.pause(); } catch { }
      }
    });
  }, [animationsEnabled, prefersReduced, activeLayer, srcA, srcB]);

  // Show hero content immediately for better performance
  useEffect(() => {
    setShowHeroContent(true);
  }, []);

  // If animations are disabled via toggle, reveal content immediately
  useEffect(() => {
    if (!animationsEnabled) setShowHeroContent(true);
  }, [animationsEnabled]);

  // (legacy sunrise/sunset helpers removed)

  // Show all content immediately for better performance
  useEffect(() => {
    setTitleStage(4); // Skip all staging delays
  }, []);

  // Cleanup any pending stage timers
  useEffect(() => {
    return () => {
      if (stageTimeoutRef.current) window.clearTimeout(stageTimeoutRef.current);
    };
  }, []);

  // Helper to get effective AUD price for sorting
  const getEffectiveAUDPrice = (pkg: Package) => {
    const dealPriceAUD = pkg.lastMinuteDealPriceAUD;
    const hasDealPrice = dealPriceAUD && dealPriceAUD > 0 && dealPriceAUD < pkg.priceAUD;
    const isDealActive = pkg.lastMinuteDealExpiryDate ? new Date(pkg.lastMinuteDealExpiryDate) > new Date() : false;
    
    if (hasDealPrice && isDealActive && dealPriceAUD) {
      return dealPriceAUD;
    }
    return pkg.priceAUD;
  };

  const rawFeaturedDestinations = homeData?.featuredDestinations || [];
  
  // Only show real live packages from API - no fallback dummy data
  // Sort: 1) Main nav destinations first (showInMainNav=true), 2) Then by price within each group
  const featuredPackages = React.useMemo(() => {
    const packages = homeData?.popularPackages || [];
    
    // Get set of destination IDs that are in main nav
    const mainNavDestIds = new Set(
      rawFeaturedDestinations.filter(d => d.showInMainNav === true).map(d => d._id)
    );
    
    return [...packages].sort((a, b) => {
      const destIdA = typeof a.destination === 'object' ? a.destination._id : a.destination;
      const destIdB = typeof b.destination === 'object' ? b.destination._id : b.destination;
      
      const aIsMainNav = mainNavDestIds.has(destIdA);
      const bIsMainNav = mainNavDestIds.has(destIdB);
      
      // Main nav destinations come first
      if (aIsMainNav && !bIsMainNav) return -1;
      if (!aIsMainNav && bIsMainNav) return 1;
      
      // Within same group, sort by price (lowest first)
      return getEffectiveAUDPrice(a) - getEffectiveAUDPrice(b);
    });
  }, [homeData?.popularPackages, rawFeaturedDestinations]);

  // Split destinations: show only those with showInMainNav=true as featured, rest go to View All
  const featuredDestinations = React.useMemo(() => {
    const packageCountByDest: { [key: string]: number } = {};

    featuredPackages.forEach((pkg: Package) => {
      const destId = typeof pkg.destination === 'object' ? pkg.destination._id : pkg.destination;
      packageCountByDest[destId] = (packageCountByDest[destId] || 0) + 1;
    });

    // Filter to only show destinations marked for main nav
    const mainNavDestinations = rawFeaturedDestinations.filter(d => d.showInMainNav === true);
    
    return [...mainNavDestinations].sort((a, b) => {
      const countA = packageCountByDest[a._id] || 0;
      const countB = packageCountByDest[b._id] || 0;
      return countB - countA; // Sort descending - more packages first
    });
  }, [rawFeaturedDestinations, featuredPackages]);

  // Check if there are more destinations to show in "View All"
  const hasMoreDestinations = rawFeaturedDestinations.length > featuredDestinations.length;

  // Group packages by destination - ONLY show main nav destinations (showInMainNav=true)
  // Other destinations (Thailand, Cambodia, etc.) are only accessible via "View All Packages"
  const packagesByDestination = React.useMemo(() => {
    const grouped: { [key: string]: { destination: Destination; packages: Package[] } } = {};

    featuredPackages.forEach((pkg: Package) => {
      const destId = typeof pkg.destination === 'object' ? pkg.destination._id : pkg.destination;
      const destination = rawFeaturedDestinations.find(d => d._id === destId);
      
      // Only include packages from main nav destinations
      if (!destination || destination.showInMainNav !== true) {
        return;
      }

      if (!grouped[destId]) {
        grouped[destId] = {
          destination,
          packages: []
        };
      }

      grouped[destId].packages.push(pkg);
    });

    // Sort by package count (more packages first)
    const sortedGroups = Object.values(grouped).sort((a, b) => {
      return b.packages.length - a.packages.length;
    });

    // Also sort packages within each group by effective AUD price (lowest first)
    sortedGroups.forEach(group => {
      group.packages.sort((a, b) => getEffectiveAUDPrice(a) - getEffectiveAUDPrice(b));
    });

    return sortedGroups;
  }, [featuredPackages, rawFeaturedDestinations]);

  // Layered hero no longer uses a static heroBackground image/video

  // Only show real testimonials from API - no fallback dummy data
  const testimonials = homeData?.testimonials || [];

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-yellow-400 to-orange-600 px-4">
        <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-white mb-6"></div>
        <p className="text-white text-xl font-semibold text-center">Loading your tropical paradise...</p>
        <p className="text-white/80 text-sm mt-2 text-center max-w-md">
          First visit may take a moment as we prepare your experience
        </p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Bali Getaway - Exclusive Resort Packages & Tropical Experiences</title>
        <meta name="description" content="Discover exclusive resort packages in Bali. Book Exclusive accommodations, experience luxury amenities, and create unforgettable memories in paradise." />
      </Helmet>

      {/* Custom Styles for Testimonials Carousel */}
      <style>{testimonialsSwiperStyles}</style>
      {/* Custom Styles for Packages Carousel */}
      <style>{packagesSwiperStyles}</style>

      <div className="min-h-screen">
        {/* Cinematic Video Hero Section */}
        <section className="relative min-h-[calc(100vh-4rem)] overflow-hidden">
          {/* Cinematic Background Video with Smooth Crossfade */}
          <div className="absolute inset-0">
            <motion.video
              ref={bgARef}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              src={srcA}
              muted
              loop={false}
              playsInline
              autoPlay={animationsEnabled && !prefersReduced}
              preload="auto"
              poster="/logo.png"
              onEnded={() => startCrossfade(0)}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={animationsEnabled && !prefersReduced ? { opacity: activeLayer === 0 ? 1 : 0, scale: activeLayer === 0 ? 1.06 : 1.02 } : { opacity: activeLayer === 0 ? 1 : 0, scale: 1 }}
              transition={{ opacity: { duration: FADE_MS / 1000, ease: 'easeInOut' }, scale: { duration: 45, ease: 'linear' } }}
              style={{ willChange: 'opacity, transform' }}
            />
            <motion.video
              ref={bgBRef}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              src={srcB}
              muted
              loop={false}
              playsInline
              autoPlay={animationsEnabled && !prefersReduced}
              preload="auto"
              poster="/logo.png"
              onEnded={() => startCrossfade(1)}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={animationsEnabled && !prefersReduced ? { opacity: activeLayer === 1 ? 1 : 0, scale: activeLayer === 1 ? 1.06 : 1.02 } : { opacity: activeLayer === 1 ? 1 : 0, scale: 1 }}
              transition={{ opacity: { duration: FADE_MS / 1000, ease: 'easeInOut' }, scale: { duration: 45, ease: 'linear' } }}
              style={{ willChange: 'opacity, transform' }}
            />
            {/* Readability gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/10 pointer-events-none" />
            {/* Subtle vignette for cinematic edges */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 55%, rgba(0,0,0,0.45) 100%)'
              }}
            />
            {/* Letterbox bars for cinema look */}
            <div className="absolute top-0 left-0 right-0 h-16 md:h-20 bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 h-16 md:h-20 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
            {/* Fade-from-black curtain for cinematic entry */}
            {shouldAnimateText && (
              <motion.div
                initial={{ opacity: 1 }}
                animate={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
                className="absolute inset-0 bg-black pointer-events-none"
              />
            )}
          </div>

          {/* Hero Content - Left Right Layout */}
          {showHeroContent && (
            <div className="relative z-10 min-h-[calc(100vh-4rem)] flex items-center justify-center">
              <div className="container mx-auto px-6 lg:px-8">
                <div className="flex items-center justify-center">

                  {/* Left Side - Content */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-white space-y-8 text-center max-w-4xl mx-auto flex flex-col items-center justify-center h-full"
                  >
                    {/* Removed logo-with-text badge to avoid duplication with header */}

                    {/* Main Heading - Cinematic staged word-by-word reveal */}
                    {(() => {
                      const line1 = ['Unbeatable', 'Stays'];
                      const line2 = ['Unforgettable', 'Bali'];
                      const lineVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.18 } } } as const;
                      const wordVariants = {
                        hidden: { y: 20, opacity: 0, filter: 'blur(4px)' },
                        visible: { y: 0, opacity: 1, filter: 'blur(0px)', transition: { duration: 0.6, ease: 'easeOut' } }
                      } as const;
                      return (
                        <h1 className="text-5xl lg:text-7xl font-bold leading-tight font-display tracking-tight">
                          {/* Line 1 */}
                          {shouldAnimateText ? (
                            <motion.span
                              className="block"
                              initial="hidden"
                              animate={titleStage >= 1 ? 'visible' : 'hidden'}
                              variants={lineVariants}
                              onAnimationComplete={() => {
                                if (titleStage === 1) scheduleStage(2, 250);
                              }}
                            >
                              {line1.map((w, i) => (
                                <motion.span key={`l1-${i}`} className="inline-block mr-3" variants={wordVariants}>
                                  {w}
                                </motion.span>
                              ))}
                            </motion.span>
                          ) : (
                            <span className="block">{line1.join(' ')}</span>
                          )}

                          {/* Line 2 */}
                          {shouldAnimateText ? (
                            <motion.span
                              className="block"
                              initial="hidden"
                              animate={titleStage >= 2 ? 'visible' : 'hidden'}
                              variants={lineVariants}
                              onAnimationComplete={() => {
                                if (titleStage === 2) scheduleStage(3, 300);
                              }}
                            >
                              {line2.map((w, i) => (
                                <motion.span
                                  key={`l2-${i}`}
                                  className="inline-block mr-3 bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent"
                                  variants={wordVariants}
                                >
                                  {w}
                                </motion.span>
                              ))}
                            </motion.span>
                          ) : (
                            <span className="block bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">{line2.join(' ')}</span>
                          )}
                        </h1>
                      );
                    })()}

                    {/* Subtitle - Cinematic word-by-word reveal (Stage 3) */}
                    {(() => {
                      const subtitle = 'Introducing Bali Getaway – Exclusive Stays at Prices You’ll Love.';
                      const words = subtitle.split(' ');
                      const container = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } } as const;
                      const word = { hidden: { y: 10, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { duration: 0.4 } } } as const;
                      if (shouldAnimateText) {
                        return (
                          <motion.p
                            className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-2xl mx-auto"
                            initial="hidden"
                            animate={titleStage >= 3 ? 'visible' : 'hidden'}
                            variants={container}
                            onAnimationComplete={() => { if (titleStage === 3) scheduleStage(4, 400); }}
                          >
                            {words.map((w, i) => (
                              <motion.span key={`sub-${i}`} className="inline-block mr-1" variants={word}>
                                {w}
                              </motion.span>
                            ))}
                          </motion.p>
                        );
                      }
                      return (
                        <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-2xl mx-auto">{subtitle}</p>
                      );
                    })()}

                    {/* CTA Buttons */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: titleStage >= 4 ? 1 : 0, y: titleStage >= 4 ? 0 : 20 }}
                      transition={{ duration: 0.6 }}
                      className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                      <Link
                        to="/packages"
                        className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-full font-semibold text-lg shadow-2xl hover:from-yellow-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105"
                      >
                        Explore Packages
                        <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </Link>

                      <button
                        type="button"
                        onClick={() => setIsVideoOpen(true)}
                        className="group inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg border border-white/20 hover:bg-white/20 transition-all duration-300"
                      >
                        <PlayIcon className="mr-2 h-5 w-5" />
                        Watch Video
                      </button>
                    </motion.div>

                    {/* Subtle promo pill */}
                    {/* <div className="mt-4">
                      <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-sm font-medium">
                        <GiftIcon className="h-5 w-5" />
                        <span className="hidden sm:inline">Book your Bali Holiday and get a free holiday in Cambodia or Vietnam Worth A$999</span>
                        <span className="sm:hidden">Free Cambodia/Vietnam holiday A$ 999</span>

                      </div>
                    </div> */}

                  </motion.div>

                  {/* Right side indicator removed for distraction-free hero */}
                </div>
              </div>
            </div>
          )}

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white"
          >
            <motion.div
              animate={animationsEnabled ? { y: [0, 10, 0] } : { y: 0 }}
              transition={animationsEnabled ? { duration: 2, repeat: Infinity } : { duration: 0 }}
              className="flex flex-col items-center"
            >
              <span className="text-sm mb-2 opacity-80">Scroll to explore</span>
              <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
                <motion.div
                  animate={animationsEnabled ? { y: [0, 12, 0] } : { y: 0 }}
                  transition={animationsEnabled ? { duration: 2, repeat: Infinity } : { duration: 0 }}
                  className="w-1 h-3 bg-white/60 rounded-full mt-2"
                />
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Removed large free-holiday promo section per request */}

        {/* Destinations Showcase - Moved to top */}
        <section className="py-24 bg-gradient-to-br from-orange-50 via-yellow-50 to-white">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-orange-200 shadow-lg mb-6">
                <MapPinIcon className="h-5 w-5 text-orange-600 mr-2" />
                <span className="text-sm font-medium text-orange-800">Top Destinations</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                Explore <span className="bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">Bali's</span> Best
              </h2>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-8 max-w-none">
              {featuredDestinations.map((destination: Destination, index: number) => (
                <motion.div
                  key={destination._id}
                  className="group relative overflow-hidden w-full sm:w-[280px] md:w-[320px] lg:w-[350px] flex-shrink-0"
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={`/destinations/${destination._id}`}
                    className="block relative h-[420px] rounded-3xl overflow-hidden shadow-2xl group-hover:shadow-orange-500/40 transition-all duration-700 transform group-hover:-translate-y-4 group-hover:scale-105"
                  >
                    {/* Background Pattern */}
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-100 via-yellow-50 to-red-100 opacity-30"></div>

                    {/* Main Image */}
                    <img
                      src={resolveImageUrl(destination.image)}
                      alt={destination.name}
                      className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-1000 ease-out"
                    />

                    {/* Gradient Overlays */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-600/40 via-transparent to-yellow-600/40 group-hover:from-orange-600/60 group-hover:to-yellow-600/60 transition-all duration-700"></div>

                    {/* Package Count Badge - Top Left */}
                    <div className="absolute top-6 left-6 px-4 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-2xl shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-black">{destination.packageCount || 0}</span>
                        <div className="text-left">
                          <div className="text-xs font-bold uppercase tracking-wide">Packages</div>
                          <div className="text-xs opacity-90">Available</div>
                        </div>
                      </div>
                    </div>

                    {/* Location Icon - Top Right */}
                    <div className="absolute top-6 right-6 w-14 h-14 bg-white/15 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/30 group-hover:bg-white/25 group-hover:scale-110 transition-all duration-300 shadow-xl">
                      <MapPinIcon className="h-7 w-7 text-white drop-shadow-lg" />
                    </div>

                    {/* Destination Info - Bottom */}
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <div className="mb-4">
                        <h3 className="text-4xl font-black text-white mb-3 drop-shadow-2xl group-hover:text-yellow-300 transition-colors duration-300">
                          {destination.name}
                        </h3>

                        {/* Description removed per user request */}

                        {/* Action Button */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-semibold border border-white/30">
                              🏝️ Tropical Paradise
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 text-orange-300 group-hover:text-yellow-300 transition-colors font-bold">
                            <span className="text-sm uppercase tracking-wider">Explore</span>
                            <ArrowRightIcon className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-700"></div>

                    {/* Border Glow */}
                    <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-gradient-to-r group-hover:from-orange-400 group-hover:to-yellow-400 transition-all duration-500"></div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* View All Destinations Link */}
            {hasMoreDestinations && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center mt-12"
              >
                <Link
                  to="/destinations"
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-full font-semibold text-lg shadow-lg hover:from-yellow-700 hover:to-orange-700 transition-all duration-300 transform hover:scale-105"
                >
                  View All Destinations
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
            )}

            {featuredDestinations.length === 0 && (
              <div className="text-center py-20">
                <div className="max-w-md mx-auto">
                  <div className="w-24 h-24 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MapPinIcon className="h-12 w-12 text-orange-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">No Destinations Available</h3>
                  <p className="text-gray-600 leading-relaxed">We're working on adding amazing destinations for you. Please check back soon!</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Video Modal Overlay */}

        {isVideoOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseVideo();
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Bali Getaway promotional video"
          >
            <div className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <button
                type="button"
                onClick={handleCloseVideo}
                className="absolute top-3 right-3 z-10 px-3 py-1.5 bg-white/10 text-white rounded-full border border-white/20 hover:bg-white/20"
                aria-label="Close video"
              >
                Close
              </button>
              <iframe
                src="https://player.cloudinary.com/embed/?cloud_name=dyecmrs6w&public_id=Bali-Getaway-Video_csmpae&profile=cld-default"
                className="w-full h-full"
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
                title="Bali Getaway Promotional Video"
                loading="lazy"
              />
            </div>
          </div>
        )}

        {/* Modern Packages Section with Destination Tabs */}
        <section className="py-24 bg-gradient-to-br from-orange-50 to-yellow-50">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              {/* <div className="inline-flex items-center px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-orange-200 shadow-lg mb-6">
              <GiftIcon className="h-5 w-5 text-orange-600 mr-2" />
              <span className="text-sm font-medium text-orange-800">Exclusive Packages</span>
            </div> */}
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                <span className="bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">Handpicked </span> Packages
              </h2>

            </motion.div>

            {/* Packages by Destination - Main nav destinations first, then other destinations */}
            {packagesByDestination.map(({ destination, packages: destPackages }, destIndex) => (
              <div key={destination._id} className="mb-16">
                {/* Destination Header - Center Aligned */}
                <div className="flex items-center justify-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center">
                    <MapPinIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900">{destination.name}</h3>
                    <p className="text-sm text-gray-500">{destPackages.length} package{destPackages.length !== 1 ? 's' : ''} available</p>
                  </div>
                </div>

                {/* Package Cards - Grid for few packages, Carousel for many */}
                {destPackages.length <= 4 ? (
                  <div className={`grid gap-6 justify-center ${
                    destPackages.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' :
                    destPackages.length === 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto' :
                    destPackages.length === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto' :
                    'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto'
                  }`}>
                    {destPackages.map((pkg: Package, index: number) => (
                      <motion.div
                        key={pkg._id}
                        className="group relative h-full"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <div className="relative overflow-hidden rounded-2xl bg-white shadow-xl group-hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                          <div className="relative h-56 overflow-hidden">
                            <img
                              src={resolveImageUrl(pkg.bannerImage)}
                              alt={pkg.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                            {pkg.popular && (
                              <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-semibold rounded-full">
                                Popular
                              </div>
                            )}
                          </div>

                          <div className="p-5 flex flex-col flex-grow">
                            <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                              {pkg.name}
                            </h3>
                            <p className="text-gray-600 mb-4 line-clamp-2 text-sm flex-grow">
                              {pkg.description}
                            </p>
                            <div className="flex items-center justify-between mt-auto">
                              <div>
                                {(() => {
                                  const displayPrice = getDisplayPrice(pkg);
                                  return (
                                    <div>
                                      {displayPrice.isDeal && (
                                        <div className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded mb-1 inline-block">
                                          ⚡ DEAL
                                        </div>
                                      )}
                                      <div className="flex items-baseline gap-2">
                                        <div className={`text-xl font-bold ${displayPrice.isDeal ? 'text-red-600' : 'text-orange-600'}`}>
                                          {displayPrice.price}
                                        </div>
                                        {displayPrice.originalPrice && (
                                          <div className="text-sm text-gray-500 line-through">
                                            {displayPrice.originalPrice}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                              <Link
                                to={`/packages/${pkg._id}`}
                                className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-lg font-medium text-sm hover:from-yellow-600 hover:to-orange-700 transition-all duration-300 group-hover:scale-105"
                              >
                                View Details
                                <ArrowRightIcon className="ml-1 h-4 w-4" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="packages-swiper">
                    <Swiper
                      modules={[Navigation, Pagination, Autoplay]}
                      spaceBetween={24}
                      slidesPerView={1}
                      navigation={true}
                      pagination={{ clickable: true }}
                      autoplay={{
                        delay: 5000,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                      }}
                      loop={destPackages.length > 4}
                      breakpoints={{
                        640: {
                          slidesPerView: 2,
                          spaceBetween: 20,
                        },
                        1024: {
                          slidesPerView: 3,
                          spaceBetween: 24,
                        },
                        1280: {
                          slidesPerView: 4,
                          spaceBetween: 24,
                        },
                      }}
                      className="!pb-12"
                    >
                      {destPackages.map((pkg: Package, index: number) => (
                        <SwiperSlide key={pkg._id}>
                          <motion.div
                            className="group relative h-full"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.05 }}
                            viewport={{ once: true }}
                          >
                          <div className="relative overflow-hidden rounded-2xl bg-white shadow-xl group-hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                            <div className="relative h-56 overflow-hidden">
                              <img
                                src={resolveImageUrl(pkg.bannerImage)}
                                alt={pkg.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                              {pkg.popular && (
                                <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-semibold rounded-full">
                                  Popular
                                </div>
                              )}
                            </div>

                            <div className="p-5 flex flex-col flex-grow">
                              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                                {pkg.name}
                              </h3>
                              <p className="text-gray-600 mb-4 line-clamp-2 text-sm flex-grow">
                                {pkg.description}
                              </p>
                              <div className="flex items-center justify-between mt-auto">
                                <div>
                                  {(() => {
                                    const displayPrice = getDisplayPrice(pkg);
                                    return (
                                      <div>
                                        {displayPrice.isDeal && (
                                          <div className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded mb-1 inline-block">
                                            ⚡ DEAL
                                          </div>
                                        )}
                                        <div className="flex items-baseline gap-2">
                                          <div className={`text-xl font-bold ${displayPrice.isDeal ? 'text-red-600' : 'text-orange-600'}`}>
                                            {displayPrice.price}
                                          </div>
                                          {displayPrice.originalPrice && (
                                            <div className="text-sm text-gray-500 line-through">
                                              {displayPrice.originalPrice}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()
                                  }
                                </div>
                                <Link
                                  to={`/packages/${pkg._id}`}
                                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-lg font-medium text-sm hover:from-yellow-600 hover:to-orange-700 transition-all duration-300 group-hover:scale-105"
                                >
                                  View Details
                                  <ArrowRightIcon className="ml-1 h-4 w-4" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      </SwiperSlide>
                    ))}
                    </Swiper>
                  </div>
                )}
              </div>
            ))}

            {/* No packages message */}
            {featuredPackages.length === 0 && (
              <div className="text-center py-12">
                <GiftIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg text-gray-500">No packages available at the moment.</p>
              </div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Link
                to="/packages"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-600 to-orange-600 text-white rounded-full font-semibold text-lg shadow-lg hover:from-yellow-700 hover:to-orange-700 transition-all duration-300 transform hover:scale-105"
              >
                View All Packages
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Last Minute Deals Section - Image Focused */}
        {lastMinuteDeals.length > 0 && (
          <section className="py-20 bg-black relative overflow-hidden">
            {/* Dynamic Background Effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-orange-900/20 to-yellow-900/20"></div>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-10 left-10 w-40 h-40 bg-red-500/30 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-10 right-10 w-60 h-60 bg-orange-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-yellow-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
            </div>

            <div className="container mx-auto px-6 lg:px-8 relative z-10">
              {/* Minimal Header */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full shadow-2xl mb-4 animate-bounce">
                  <SparklesIcon className="h-6 w-6 mr-3" />
                  <span className="text-lg font-black uppercase tracking-widest">⚡ FLASH DEALS ⚡</span>
                </div>
                <h2 className="text-5xl lg:text-6xl font-black text-white mb-2 drop-shadow-lg">
                  <span className="bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent animate-pulse">LIMITED TIME ONLY</span>
                </h2>
                {/* <div className="flex items-center justify-center space-x-4 text-white/90">
                <div className="h-px bg-gradient-to-r from-transparent via-red-500 to-transparent flex-1 max-w-20"></div>
                <span className="text-xl font-bold">LIMITED TIME ONLY</span>
                <div className="h-px bg-gradient-to-r from-transparent via-red-500 to-transparent flex-1 max-w-20"></div>
              </div> */}
              </motion.div>

              {/* Image-Only Deal Cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {lastMinuteDeals.map((deal, index) => (
                  <motion.div
                    key={deal._id}
                    initial={{ opacity: 0, scale: 0.8, y: 50 }}
                    whileInView={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1, type: "spring", bounce: 0.3 }}
                    viewport={{ once: true }}
                    className="group cursor-pointer"
                  >
                    {deal.linkType === 'external' ? (
                      <a
                        href={deal.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-red-500/25 transition-all duration-500 transform hover:scale-105"
                      >
                        <div className="relative h-80 overflow-hidden">
                          <img
                            src={resolveImageUrl(deal.image)}
                            alt={deal.title}
                            className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-700 ease-out"
                          />

                          {/* Intense Gradient Overlays */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                          <div className="absolute inset-0 bg-gradient-to-br from-red-600/40 via-transparent to-orange-600/40 group-hover:from-red-600/60 group-hover:to-orange-600/60 transition-all duration-500"></div>

                          {/* Pulsing Discount Badge */}
                          {deal.discountBadge && (
                            <div className="absolute top-4 right-4 bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-2xl font-black text-lg shadow-2xl animate-pulse transform rotate-3 hover:rotate-0 transition-transform duration-300">
                              <div className="flex items-center space-x-1">
                                <SparklesIcon className="h-4 w-4" />
                                <span>{deal.discountBadge}</span>
                              </div>
                            </div>
                          )}

                          {/* Deal Title Overlay */}
                          <div className="absolute bottom-0 left-0 right-0 p-6">
                            <h3 className="text-2xl font-black text-white mb-2 drop-shadow-lg group-hover:text-yellow-300 transition-colors duration-300">
                              {deal.title}
                            </h3>

                            {/* CTA Button */}
                            <div className="flex items-center justify-center mt-4 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full font-bold text-sm uppercase tracking-wider shadow-xl transform group-hover:scale-110 transition-all duration-300 hover:shadow-red-500/50">
                              <span>GRAB NOW</span>
                              <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                            </div>
                          </div>

                          {/* Flash Effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-500"></div>
                        </div>
                      </a>
                    ) : (
                      <Link
                        to={deal.link}
                        className="block relative overflow-hidden rounded-3xl shadow-2xl hover:shadow-red-500/25 transition-all duration-500 transform hover:scale-105"
                      >
                        <div className="relative h-80 overflow-hidden">
                          <img
                            src={resolveImageUrl(deal.image)}
                            alt={deal.title}
                            className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-700 ease-out"
                          />

                          {/* Intense Gradient Overlays */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                          <div className="absolute inset-0 bg-gradient-to-br from-red-600/40 via-transparent to-orange-600/40 group-hover:from-red-600/60 group-hover:to-orange-600/60 transition-all duration-500"></div>

                          {/* Pulsing Discount Badge */}
                          {deal.discountBadge && (
                            <div className="absolute top-4 right-4 bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-2xl font-black text-lg shadow-2xl animate-pulse transform rotate-3 hover:rotate-0 transition-transform duration-300">
                              <div className="flex items-center space-x-1">
                                <SparklesIcon className="h-4 w-4" />
                                <span>{deal.discountBadge}</span>
                              </div>
                            </div>
                          )}

                          {/* Deal Title Overlay */}
                          <div className="absolute bottom-0 left-0 right-0 p-6">
                            <h3 className="text-2xl font-black text-white mb-2 drop-shadow-lg group-hover:text-yellow-300 transition-colors duration-300">
                              {deal.title}
                            </h3>

                            {/* CTA Button */}
                            <div className="flex items-center justify-center mt-4 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full font-bold text-sm uppercase tracking-wider shadow-xl transform group-hover:scale-110 transition-all duration-300 hover:shadow-red-500/50">
                              <span>GRAB NOW</span>
                              <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                            </div>
                          </div>

                          {/* Flash Effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-500"></div>
                        </div>
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Urgency Footer */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                viewport={{ once: true }}
                className="text-center mt-12"
              >
                <div className="inline-flex items-center space-x-4 px-8 py-4 bg-gradient-to-r from-red-600/20 to-orange-600/20 backdrop-blur-sm rounded-full border border-red-500/30">
                  <div className="flex items-center space-x-2 text-red-400">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="font-bold text-lg">HURRY!</span>
                  </div>
                  <span className="text-white/90 font-medium">- limited time deal</span>
                  <div className="flex items-center space-x-2 text-orange-400">
                    <span className="font-bold text-lg">ACT FAST!</span>
                    <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        )}


        {/* Testimonials Section */}
        <section className="py-24 bg-gradient-to-br from-yellow-50 to-orange-100">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center px-4 py-2 bg-white/90 backdrop-blur-sm rounded-full border border-orange-200 shadow-sm mb-6">
                <StarIcon className="h-5 w-5 text-orange-600 mr-2" />
                <span className="text-sm font-medium text-orange-800">Guest Reviews</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                What Our <span className="bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">Guests</span> Say
              </h2>

            </motion.div>

            {/* Testimonials Carousel */}
            <div className="relative">
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 40,
                  },
                }}
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                pagination={{
                  clickable: true,
                  bulletClass: 'swiper-pagination-bullet !bg-orange-300 !opacity-50',
                  bulletActiveClass: 'swiper-pagination-bullet-active !bg-orange-500 !opacity-100'
                }}
                navigation={{
                  nextEl: '.testimonials-next',
                  prevEl: '.testimonials-prev'
                }}
                loop={testimonials.length > 3}
                className="testimonials-swiper pb-12"
              >
                {testimonials.map((testimonial: Testimonial, index: number) => (
                  <SwiperSlide key={testimonial._id}>
                    <motion.div
                      className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 h-full"
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <div className="flex items-center mb-4">
                        {[...Array(5)].map((_, starIndex) => (
                          <StarIcon
                            key={starIndex}
                            className={`h-5 w-5 ${starIndex < testimonial.rating
                                ? 'text-yellow-500 fill-current'
                                : 'text-gray-300'
                              }`}
                          />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed">
                        "{testimonial.review}"
                      </p>

                      {/* Package & Resort Info */}
                      {(testimonial.packageName || testimonial.resortName) && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {testimonial.packageName && (
                            <span className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                              📦 {testimonial.packageName}
                            </span>
                          )}
                          {testimonial.resortName && (
                            <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                              🏨 {testimonial.resortName}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Customer Info */}
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                          {testimonial.customerName.charAt(0)}
                        </div>
                        <div className="ml-4">
                          <div className="font-semibold text-gray-900">{testimonial.customerName}</div>
                          <div className="text-sm text-gray-600">📍 {testimonial.location}</div>
                        </div>
                      </div>
                    </motion.div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Custom Navigation Buttons */}
              <button className="testimonials-prev absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-orange-600 hover:bg-orange-50 transition-colors group">
                <ArrowRightIcon className="w-5 h-5 rotate-180 group-hover:scale-110 transition-transform" />
              </button>
              <button className="testimonials-next absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-orange-600 hover:bg-orange-50 transition-colors group">
                <ArrowRightIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-24 bg-gradient-to-br from-orange-600 via-yellow-600 to-red-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center text-white"
            >
              <SparklesIcon className="h-16 w-16 text-yellow-300 mx-auto mb-6" />
              <h2 className="text-4xl lg:text-6xl font-bold mb-6">
                Start Your
                <span className="block">Bali Journey</span>
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                Start your journey to paradise with our exclusive resort packages and create memories that will last a lifetime.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/packages"
                  className="inline-flex items-center px-8 py-4 bg-white text-orange-600 rounded-full font-semibold text-lg shadow-xl hover:bg-orange-50 transition-all duration-300 transform hover:scale-105"
                >
                  Book Now
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  Contact Us
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </motion.div>
          </div>
          {/* Background Animation */}
          <motion.div
            className="absolute top-20 left-10 w-32 h-32 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl opacity-30"
            animate={{
              x: [0, 50, 0],
              y: [0, -30, 0],
              scale: [1, 1.2, 1]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-40 h-40 bg-orange-400 rounded-full mix-blend-multiply filter blur-xl opacity-30"
            animate={{
              x: [0, -40, 0],
              y: [0, 40, 0],
              scale: [1, 0.8, 1]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </section>
      </div>
    </>
  );
};

export default HomePage;

